import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../i18n';
import { api } from './api';
import { logger } from '../lib/logger';
import { getToken, setToken, clearToken, onTokenChange } from './tokenStorage';

const AuthCtx = createContext(null);

// ★ 2026-08-26 (Fase 55) — Applica la lingua salvata nel profilo utente,
// senza sovrascrivere una scelta esplicita locale se già presente.
// Se l'utente ha appena scelto la lingua manualmente (gp_locale_chosen='1'),
// rispettiamo quella scelta. Altrimenti applichiamo la lingua profilo.
function applyProfileLanguage(user) {
  const preferred = user?.preferred_language;
  if (!preferred) return;
  try {
    // ★ Fase 32 — Utenti con tenant CRM bloccato (es. rete ES): la lingua del
    // profilo è OBBLIGATORIA e ignora qualsiasi scelta locale precedente.
    const tenantLocked = Boolean(user?.crm_tenant_locked);
    const localChosen = localStorage.getItem('gp_locale_chosen') === '1';
    const currentLocal = localStorage.getItem('gp_locale');
    if (!tenantLocked && localChosen && currentLocal && currentLocal === i18n.language) {
      // La scelta locale esplicita ha priorità sulla lettura del profilo.
      return;
    }
    if (preferred !== i18n.language) {
      i18n.changeLanguage(preferred);
      try {
        localStorage.setItem('gp_locale', preferred);
        localStorage.setItem('gp_locale_chosen', '1');
      } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    api
      .get('/auth/me')
      .then((r) => {
        if (!cancelled) {
          setUser(r.data);
          applyProfileLanguage(r.data);
        }
      })
      .catch((err) => {
        // Invalid/expired token — clear it and stay logged out.
        if (err?.response?.status && err.response.status !== 401) {
          logger.error('auth-context: /auth/me failed', err);
        }
        clearToken();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // Intentionally empty: run only on mount. `api` and setters are stable.
  }, []);

  // ★ Cross-tab session sync (Fase 59.P1):
  //   • token cancellato in un'altra tab → logout locale immediato
  //   • token ricevuto da un'altra tab (login o handoff verso una tab nuova)
  //     → carica il profilo senza chiedere un nuovo login
  useEffect(() => {
    const unsub = onTokenChange((newToken) => {
      if (!newToken) {
        setUser(null);
        return;
      }
      setUser((current) => {
        if (!current) {
          api.get('/auth/me')
            .then((r) => {
              setUser(r.data);
              applyProfileLanguage(r.data);
            })
            .catch(() => { /* token non valido → resta sloggato */ })
            .finally(() => setLoading(false));
        }
        return current;
      });
    });
    return unsub;
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    applyProfileLanguage(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  /**
   * Direct session installer — used by the magic-link / forgot-password flow.
   * The setup-account backend returns a fresh {token, user} after the user
   * sets their password, so we bypass /auth/login and install it directly.
   */
  const setSession = useCallback(({ token, user: nextUser }) => {
    if (token) setToken(token);
    if (nextUser) {
      setUser(nextUser);
      applyProfileLanguage(nextUser);
    }
  }, []);

  // Memoize the context value so consumers do not re-render on each parent render.
  const value = useMemo(
    () => ({ user, loading, login, logout, setUser, setSession }),
    [user, loading, login, logout, setSession],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
